export interface TrialBalanceOption {
  id: string;
  refNo: string;
  /** The trial balance's own description (the API's TrialBalanceRow sends it). */
  description?: string;
  label: string;
  fromDate: string;
  toDate: string;
  typeId: number;
  accountingPeriodId: string | null;
  periodLabel: string;
  /** Debits equal credits (the API's TrialBalanceRow.isBalanced); the report form offers balanced ones only. */
  isBalanced?: boolean;
}

export interface EmailDraft {
  to: string;
  subject: string;
  body: string;
  attachmentName: string;
}

export interface TrialBalanceReportRow {
  code: string;
  name: string;
  debit: number;
  credit: number;
}

export interface TrialBalanceReport {
  trialBalanceId: string;
  clientId: string;
  clientName: string;
  refNo: string;
  fromDate: string;
  toDate: string;
  accountingPeriodId: string | null;
  rows: TrialBalanceReportRow[];
  totalDebit: number;
  totalCredit: number;
  email: EmailDraft;
}

export interface NominalLedgerEntry {
  particular: string;
  refNo: string;
  debit: number;
  credit: number;
}

export interface NominalLedgerAccount {
  code: string;
  name: string;
  totalDebit: number;
  totalCredit: number;
  /** BUG-053: the closing balance is carried to the side opposite the balance. */
  closingDebit: number;
  closingCredit: number;
  /** The account's balancing figure, printed under both sides. */
  grandTotal: number;
  entries: NominalLedgerEntry[];
}

export interface NominalLedgerReport {
  trialBalanceId: string;
  clientId: string;
  clientName: string;
  refNo: string;
  accounts: NominalLedgerAccount[];
  totalDebit: number;
  totalCredit: number;
}

export type StatementLineStyle = 'heading' | 'line' | 'subtotal' | 'total' | 'blank';

export interface StatementLine {
  label: string;
  note: string;
  current: number | null;
  previous: number | null;
  style: StatementLineStyle;
}

export interface CashFlowReport {
  trialBalanceId: string;
  previousTrialBalanceId: string | null;
  clientId: string;
  clientName: string;
  currentHeader: string;
  previousHeader: string;
  lines: StatementLine[];
}

/** 0 Annual Full Accounts (HMRC), 1 Management Accounts. */
export enum ReportTypeCode {
  Annual = 0,
  Management = 1,
}

export interface ReportRow {
  id: string;
  refNo: string;
  fromDate: string;
  toDate: string;
  type: string;
  typeId: number;
  description: string;
  trialBalances: string;
  currentTrialBalanceId: string;
  previousTrialBalanceId: string | null;
  updated: string;
  submitStatus: string;
  submitStatusId: number;
  capisignStatus: string;
  /** 0 Not sent, 1 Sent, 2 Signed, 3 Declined. Capisign is not connected yet, so this is 0 for every firm today. */
  capisignStatusId: number;
  chValidationStatus: string;
  /** 0 Not validated, 1 Valid, 2 Has issues - see ReportsService.validateReport. */
  chValidationStatusId: number;
  /**
   * prod-p3: false when the report can no longer be produced because the trial balance it was
   * built from has been deleted. The row is still listed - it is the firm's record that the report
   * was issued - but the grid marks it and the actions that would fail are disabled.
   */
  canProduce: boolean;
  blockedReason: string | null;
  /** APT342: 0 the figures come from the trial balance as saved (Source TB), 1 from the Adjusted Trial Balance of the accounting period. Absent from an older API: 0. */
  balanceSource?: number;
  /** APT342: what the report was generated from, ready to show - "Source trial balance TB-1" or "Adjusted trial balance 01-Apr-2024 to 31-Mar-2025". */
  sourceLabel?: string;
  /** APT342: the adjusted balances have moved on (a journal was posted or reversed, the import changed) since the report was generated. */
  sourceChanged?: boolean;
  /** APT342: the accounting period whose Adjusted Trial Balance the report used; null for a Source TB report. */
  sourceAccountingPeriodId?: string | null;
  /** APT342: why sourceChanged is set - "2 journals posted since this report was generated - regenerate to include them."; null while the report is up to date. */
  sourceChangedReason?: string | null;
}

/** APT342 (audit trail): one adjustment journal that formed part of an Adjusted Trial Balance, as it stood when a report was generated. */
export interface ReportSourceJournal {
  journalId: string;
  journalNo: number;
  /** yyyy-MM-dd. */
  journalDate: string;
  typeCode: string;
  narrative: string | null;
  /** 1 Posted, 2 Reversed, 3 Reversal. */
  status: number;
  statusLabel: string;
  totalDebit: number;
  totalCredit: number;
  postedAtUtc: string | null;
}

/**
 * APT342 (audit trail): what one generation of a report was made from. Kept for every generation and regeneration, never overwritten.
 * A Source TB report has the trial balance and when it was last saved (its version) but no mapping point, version or journals.
 */
export interface ReportSourceCapture {
  captureId: string;
  /** 1, 2, 3 ... in the order the captures were made. */
  captureNo: number;
  /** 1 generated (the report was created), 2 saved again, 3 regenerated. */
  captureReason: number;
  reasonLabel: string;
  /** 0 the trial balance as saved, 1 the Adjusted Trial Balance of accountingPeriodId. */
  balanceSource: number;
  sourceTrialBalanceId: string;
  sourceTrialBalanceRef: string;
  sourceTrialBalanceUpdatedAtUtc: string | null;
  sourceImportBatchId: string | null;
  accountingPeriodId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** The Adjusted Trial Balance's version at that moment; null for a trial balance source. */
  balanceVersion: number | null;
  /** The latest change to the period's nominal mapping and how many accounts were mapped; null for a trial balance source. */
  mappingChangedAtUtc: string | null;
  mappedAccountCount: number | null;
  journalCount: number;
  capturedAtUtc: string;
  capturedByName: string | null;
  journals: ReportSourceJournal[];
}

/** APT342 (data lineage): Source TB -> Nominal Mapping -> Adjustments -> Adjusted TB -> Report, for one report (GET Reports/getSource/{id}). */
export interface ReportSourceLineage {
  reportId: string;
  refNo: string;
  balanceSource: number;
  /** "Source trial balance TB-1" or "Adjusted trial balance 01-Apr-2024 to 31-Mar-2025". */
  sourceLabel: string;
  currentTrialBalanceId: string;
  currentTrialBalanceRef: string;
  sourceAccountingPeriodId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** The period's Adjusted Trial Balance version now, and when its balances were last rebuilt; null for a trial balance source. */
  currentBalanceVersion: number | null;
  currentBalancesAsOfUtc: string | null;
  /** The adjusted balances have moved on since the latest capture: regenerate the report to use them. */
  sourceChanged: boolean;
  sourceChangedReason: string | null;
  /** Null for a report generated before captures were kept. */
  latest: ReportSourceCapture | null;
  /** The journals in the Adjusted Trial Balance now that the latest capture does not list. */
  journalsSince: ReportSourceJournal[];
  /** Every capture, newest first. */
  history: ReportSourceCapture[];
  /** What the capture does not record, in words. */
  note: string | null;
}

export interface ReportDetail {
  id: string;
  clientId: string;
  reportType: number;
  refNo: string;
  description: string;
  currentTrialBalanceId: string;
  showComparative: boolean;
  previousTrialBalanceId: string | null;
  directorSignDate: string | null;
  accountantSignDate: string | null;
  letterOfEngagementDate: string | null;
  clientApprovalDate: string | null;
  /** 0 Full, 1 Abridged, 2 Filleted (Companies House). Always 0 for Management Accounts. */
  accountsVariant: number;
  /** Round 3: 0 revised by replacement, 1 revised with a supplementary note, 2 New Accounts; null for a report saved before. */
  accountsType?: number | null;
  /** APT342: 0 Source TB, 1 Adjusted TB - and, for 1, the lineage: the accounting period, the period's balance version and when it was captured. */
  balanceSource?: number;
  sourceAccountingPeriodId?: string | null;
  sourceBalanceVersion?: number | null;
  sourceCapturedAtUtc?: string | null;
}

/** One rule this report was checked against - see ReportsService.validateReport. */
export interface ReportValidationCheck {
  code: string;
  message: string;
  isBlocking: boolean;
  /**
   * BUG-047: what the validation status report calls it - Blocking, Error (a disclosure the regulations require; the
   * preparer may still preview) or Warning. Absent from an older API: then Blocking or Warning by isBlocking.
   */
  level?: ReportValidationLevel;
}

export type ReportValidationLevel = 'Blocking' | 'Error' | 'Warning';

/**
 * The Companies House filing rules that don't need iXBRL - registration number format,
 * small-company eligibility for Abridged/Filleted, and the trial balance balancing.
 */
export interface ReportValidationResult {
  isValid: boolean;
  checks: ReportValidationCheck[];
}

export interface ReportTypeOption {
  id: number;
  name: string;
  /** The group header this option prints under ("Annual Reports"); null for an ungrouped list. */
  group: string | null;
}

/**
 * APT342: an accounting period whose Adjusted Trial Balance can be the source of the report's figures. Choosing it saves the report
 * against `trialBalanceId` (the period's own trial balance: it gives the report its reference and dates) with balanceSource 1.
 * The API lists only the client's periods that have an adjusted trial balance.
 */
export interface AdjustedTrialBalanceSource {
  accountingPeriodId: string;
  /** "Adjusted trial balance 01-Apr-2024 to 31-Mar-2025". */
  label: string;
  fromDate: string;
  toDate: string;
  trialBalanceId: string;
  trialBalanceRef: string;
  /** How many accounts carry an adjusted balance, and how many posted journals are in it. */
  accountCount: number;
  journalCount: number;
}

export interface ReportFormOptions {
  nextRefNo: string;
  reportTypes: ReportTypeOption[];
  /** APT342: the periods whose Adjusted Trial Balance can be chosen; empty or absent when none has one. */
  adjustedSources?: AdjustedTrialBalanceSource[];
}

export interface SaveReportPayload {
  clientId: string;
  id: string | null;
  reportType: number;
  refNo: string;
  description: string;
  currentTrialBalanceId: string;
  showComparative: boolean;
  previousTrialBalanceId: string | null;
  directorSignDate: string | null;
  accountantSignDate: string | null;
  letterOfEngagementDate: string | null;
  clientApprovalDate: string | null;
  accountsVariant: number;
  /** Round 3: the Accounts Type chosen on Add Report - 0, 1 (revised) or 2 (New Accounts); null keeps what is saved. */
  accountsType?: number | null;
  /** APT246: Related Notes is Auto - switch on the accounting policies the current trial balance gives figures for. */
  autoCheckNotes?: boolean;
  /** APT342: 1 takes the figures from the Adjusted Trial Balance of the current trial balance's period; left out for the trial balance as saved. */
  balanceSource?: number;
}

export interface ReportPageOption {
  pageId: number;
  name: string;
  full: boolean;
  abridged: boolean;
  filleted: boolean;
  /** BUG-051: whether Report Settings offers the page for that report type (otherwise it has no checkbox). */
  allowedFull: boolean;
  allowedAbridged: boolean;
  allowedFilleted: boolean;
}

export interface ReportOptions {
  clientId: string;
  showReportsDate: boolean;
  showDraftMessage: boolean;
  showAccountantOnCoverPage: boolean;
  columnHeaderStyle: string;
  hideStatutoryDeclarations: boolean;
  columnHeaderStyles: string[];
  pages: ReportPageOption[];
}

export interface SaveReportOptionsPayload {
  clientId: string;
  showReportsDate: boolean;
  showDraftMessage: boolean;
  showAccountantOnCoverPage: boolean;
  columnHeaderStyle: string;
  hideStatutoryDeclarations: boolean;
  pages: { pageId: number; full: boolean; abridged: boolean; filleted: boolean }[];
  /** #176: 0 Annual, 1 Management - each report type keeps its own options. */
  reportType?: number;
}

export interface SendEmailPayload {
  to: string;
  /** BUG-052: optional copy and blind-copy recipients. */
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
}

export interface EmailSendResponse {
  emailLogId: string;
  status: string;
  message: string;
}

/** cap3-e: bulk email of the selected reports; to, subject and body override each report's defaults. */
export interface BulkEmailReportsPayload {
  reportIds: string[];
  to?: string | null;
  cc?: string | null;
  bcc?: string | null;
  subject?: string | null;
  body?: string | null;
}

export interface BulkEmailReportResult {
  reportId: string;
  sent: boolean;
  emailLogId: string | null;
  status: string;
  message: string;
}

export interface BulkEmailReportsResponse {
  requested: number;
  sent: number;
  failed: number;
  results: BulkEmailReportResult[];
}

/** Data for the send dialog (SendTrialBalanceComponent), shared by the trial balance and accounts reports. */
export interface SendEmailDialogData {
  title: string;
  draft: EmailDraft;
  send: (payload: SendEmailPayload) => Promise<EmailSendResponse>;
  downloadAttachment: () => Promise<boolean>;
}

export interface EmailLogRow {
  id: string;
  /** Server-formatted UTC text; kept for the export. The grid shows createdAtUtc instead. */
  date: string;
  /** prod-p2: the raw UTC instant, rendered in the reader's own time zone. */
  createdAtUtc: string;
  sentTo: string;
  subject: string;
  attachment: string;
  status: string;
  user: string;
  view: string;
  clientName: string;
}

export interface EmailLogQuery {
  search: string | null;
  /** 0-7 the preset ranges, 8 the custom From/To dates (BUG-060). */
  filter: number;
  /** BUG-060: first and last day (yyyy-MM-dd, inclusive) of the custom range. */
  fromDate?: string | null;
  toDate?: string | null;
  /** BUG-059: the client the log is for; omitted for the firm-wide log. */
  clientId?: string | null;
}

/** "£ 12,400.40", or "-" for zero, as the report grids show amounts. */
export function formatMoney(value: number | null | undefined): string {
  if (!value) {
    return '-';
  }
  return `£ ${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Whole pounds with negatives in brackets, as statements present figures. */
export function formatStatement(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  const rounded = Math.round(Math.abs(value)).toLocaleString('en-GB');
  return value < 0 && Math.round(Math.abs(value)) !== 0 ? `(${rounded})` : rounded;
}
